import { useDndContext, useDroppable } from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { useBuilder } from '../lib/builderStore';
import {
  CATEGORY_TINT, NOT_REPEATABLE, QUESTION_TYPES, typeMeta,
  type FormElement, type FormPage, type QuestionType,
} from '../lib/types';
import { Badge, IconButton } from '../components/ui';
import { Icon, type IconName } from '../components/icons';
import { COLUMNS, MAX_PER_ROW, SPAN, rowsOf, widthOf } from '../lib/rowLayout';
import { InlineEditor, useElementEdit } from './InlineEditor';

/**
 * The canvas assumes a DndContext supplied by the Builder page, because dragging spans
 * the palette and every page. Each page is both a droppable and its own sortable list.
 */
export function Canvas({ onQuickAdd }: { onQuickAdd: (pageRef: string) => void }) {
  const definition = useBuilder((s) => s.definition);
  const addPage = useBuilder((s) => s.addPage);

  if (!definition) return null;

  return (
    <div className="mx-auto max-w-3xl px-6 py-6">
      {definition.pages.map((page, index) => (
        <PageBlock
          key={page.ref}
          page={page}
          index={index}
          total={definition.pages.length}
          onQuickAdd={onQuickAdd}
        />
      ))}

      <button
        onClick={addPage}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border
                   border-dashed border-[var(--ff-border-strong)] py-3 text-sm
                   text-[var(--ff-text-muted)] transition-colors
                   hover:border-[var(--ff-primary)] hover:bg-[var(--ff-surface)]
                   hover:text-[var(--ff-primary)]"
      >
        <Icon name="plus" /> Add page
      </button>
    </div>
  );
}

function PageBlock({
  page, index, total, onQuickAdd,
}: { page: FormPage; index: number; total: number; onQuickAdd: (pageRef: string) => void }) {
  const apply = useBuilder((s) => s.apply);
  const removePage = useBuilder((s) => s.removePage);
  const activePageRef = useBuilder((s) => s.activePageRef);
  const setActivePage = useBuilder((s) => s.setActivePage);

  const { setNodeRef, isOver } = useDroppable({
    id: `page:${page.ref}`,
    data: { kind: 'page', pageRef: page.ref },
  });

  const isActive = activePageRef === page.ref;
  const questionCount = page.elements.filter((e) => e.kind === 'question').length;

  return (
    <section
      ref={setNodeRef}
      onClick={() => setActivePage(page.ref)}
      aria-label={page.title ?? `Page ${index + 1}`}
      className={clsx(
        'mb-4 rounded-xl border bg-[var(--ff-surface)] shadow-sm transition-colors',
        isOver
          ? 'border-[var(--ff-primary)] ring-2 ring-[var(--ff-primary)]/20'
          : isActive
            ? 'border-[var(--ff-border-strong)]'
            : 'border-[var(--ff-border)]',
      )}
    >
      <header className="flex items-center gap-2 border-b border-[var(--ff-border)] px-3 py-2">
        <span className={clsx(
          'grid h-6 w-6 shrink-0 place-items-center rounded-md text-[11px] font-bold',
          isActive
            ? 'bg-[var(--ff-primary)] text-[var(--ff-on-primary)]'
            : 'bg-[var(--ff-surface-sunken)] text-[var(--ff-text-muted)]',
        )}>
          {index + 1}
        </span>

        <input
          value={page.title ?? ''}
          onChange={(e) => apply('Rename page', (draft) => {
            const target = draft.pages.find((p) => p.ref === page.ref);
            if (target) target.title = e.target.value;
          })}
          aria-label={`Title for page ${index + 1}`}
          placeholder="Untitled page"
          className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1.5
                     py-1 text-sm font-semibold hover:border-[var(--ff-border)]
                     focus:border-[var(--ff-border-strong)]"
        />

        <span className="hidden text-[11px] text-[var(--ff-text-muted)] sm:inline">
          {questionCount} {questionCount === 1 ? 'question' : 'questions'}
        </span>
        <code className="hidden text-[11px] text-[var(--ff-text-muted)] md:inline">
          {page.ref}
        </code>

        {total > 1 && (
          <IconButton label={`Delete page ${index + 1}`} onClick={() => removePage(page.ref)}>
            <Icon name="trash" />
          </IconButton>
        )}
      </header>

      <div className="p-2.5">
        {page.elements.length === 0 ? (
          <div className={clsx(
            'rounded-lg border border-dashed px-4 py-8 text-center text-sm transition-colors',
            isOver
              ? 'border-[var(--ff-primary)] bg-[var(--ff-primary)]/5 text-[var(--ff-primary)]'
              : 'border-[var(--ff-border)] text-[var(--ff-text-muted)]',
          )}>
            {isOver ? 'Drop to add here' : 'Drag a question type here, or use the button below.'}
          </div>
        ) : (
          <SortableContext
            items={page.elements.map((e) => e.ref)}
            strategy={verticalListSortingStrategy}
          >
            {/*
              Laid out in the rows the runtime will draw, not as a flat stack.
              Two half-width questions sit side by side here because that is where they
              will be on the form — the canvas was previously stacking them, so the only
              way to see a layout was to open Preview.
            */}
            <ul className="space-y-1.5">
              {rowsOf(page.elements).map((row) => (
                <li key={row[0]!.ref}>
                  <ul
                    className="grid gap-1.5"
                    style={{ gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))` }}
                  >
                    {row.map((element) => (
                      <li
                        key={element.ref}
                        className="min-w-0"
                        style={{
                          gridColumn: `span ${element.kind === 'group'
                            ? COLUMNS
                            : SPAN[widthOf(element)]}`,
                        }}
                      >
                        {element.kind === 'group'
                          ? <GroupCard element={element} />
                          : (
                            <SortableItem
                              element={element}
                              position={page.elements.indexOf(element) + 1}
                              rowSize={row.length}
                            />
                          )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </SortableContext>
        )}

        {/*
          Every page gets its own add button. Without this the palette always appended to
          whichever page happened to be last, which made adding to page 1 of a four-page
          form impossible without dragging.
        */}
        <button
          onClick={(e) => { e.stopPropagation(); onQuickAdd(page.ref); }}
          className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border
                     border-dashed border-[var(--ff-border)] py-2 text-[13px]
                     text-[var(--ff-text-muted)] transition-colors
                     hover:border-[var(--ff-primary)] hover:text-[var(--ff-primary)]"
        >
          <Icon name="plus" size={14} /> Add question to this page
        </button>
      </div>
    </section>
  );
}

/**
 * A group on the canvas, with its children.
 *
 * Rendered as a container rather than an ordinary question card: a group holds several
 * questions, and drawing it as one empty box invites somebody to delete it. Children are
 * selectable and removable in place, so the group is edited where it is rather than through
 * a separate panel.
 */
function GroupCard({ element }: { element: FormElement }) {
  const children = element.elements ?? [];
  const repeat = element.repeat;
  const columns = element.layout?.columns ?? 1;

  // Its own drop target, so a question can be dragged from the palette straight into the
  // group. Before this the only way in was the select underneath, which is not where
  // anybody looks after dragging everything else onto the canvas.
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `group:${element.ref}`,
    data: { kind: 'group', groupRef: element.ref },
  });

  const selectedRef = useBuilder((s) => s.selectedRef);
  const select = useBuilder((s) => s.select);
  const removeElement = useBuilder((s) => s.removeElement);
  const addToGroup = useBuilder((s) => s.addToGroup);

  const selected = selectedRef === element.ref;

  return (
    <li
      ref={setDropRef}
      onClick={(e) => { e.stopPropagation(); select(element.ref); }}
      className={clsx(
        'rounded-lg border p-3 transition-colors',
        isOver && 'ring-2 ring-[var(--ff-primary)]',
        selected
          ? 'border-[var(--ff-primary)] bg-[var(--ff-surface)]'
          : 'border-[var(--ff-border)] bg-[var(--ff-surface-sunken)]',
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <Icon name="copy" size={14} className="text-[var(--ff-text-muted)]" />
        <span className="flex-1 truncate text-[13px] font-medium">
          {element.title || element.ref}
        </span>
        {repeat
          ? <Badge tone="primary">repeats {repeat.min}–{repeat.max}</Badge>
          : <Badge>{columns > 1 ? `${columns} columns` : 'group'}</Badge>}
        <IconButton
          label="Delete group"
          onClick={(e) => { e.stopPropagation(); removeElement(element.ref); }}
        >
          <Icon name="trash" size={14} />
        </IconButton>
      </div>

      {/* Laid out the way the form will be, so the canvas answers "what will this look
          like" without a round trip through Preview. */}
      <ul
        className={clsx('gap-1', columns > 1 ? 'grid' : 'space-y-1')}
        style={columns > 1
          ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
          : undefined}
      >
        <SortableContext
          items={children.map((c) => c.ref)}
          strategy={verticalListSortingStrategy}
        >
          {children.map((child) => <GroupChild key={child.ref} child={child} />)}
        </SortableContext>
      </ul>

      {children.length === 0 && (
        <p className="py-2 text-xs text-[var(--ff-text-muted)]">
          Drag a question from the palette onto this block, or use the menu below.
        </p>
      )}

      {/* Only the types the server will accept inside a repeat. The server refuses the
          rest at publish; offering them here would just move the rejection later. */}
      <select
        value=""
        aria-label="Add a question to this group"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          if (e.target.value) addToGroup(element.ref, e.target.value as QuestionType);
          e.target.value = '';
        }}
        className="mt-2 w-full rounded-md border border-dashed border-[var(--ff-border)]
                   bg-transparent px-2 py-1.5 text-[13px] text-[var(--ff-text-muted)]"
      >
        <option value="">+ Add a question to this group</option>
        {QUESTION_TYPES
          .filter((t) => !t.unavailable && !NOT_REPEATABLE.has(t.type))
          .map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
      </select>
    </li>
  );
}

/**
 * One question inside a layout group.
 *
 * Draggable, which it was not. The row rendered as a plain list item with a delete button,
 * so once a question was inside a group there was no way to pick it up again — the only
 * route out was to delete it and rebuild it somewhere else. Everything else on this canvas
 * moves by dragging, so a row that cannot reads as broken rather than as deliberate.
 */
function GroupChild({ child }: { child: FormElement }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: child.ref, data: { kind: 'element', ref: child.ref } });

  const selectedRef = useBuilder((s) => s.selectedRef);
  const select = useBuilder((s) => s.select);
  const removeElement = useBuilder((s) => s.removeElement);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      onClick={(e) => { e.stopPropagation(); select(child.ref); }}
      className={clsx(
        'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[13px]',
        isDragging && 'opacity-40',
        selectedRef === child.ref
          ? 'border-[var(--ff-primary)] bg-[var(--ff-surface-raised)]'
          : 'border-[var(--ff-border)] bg-[var(--ff-surface)]',
      )}
    >
      {/* The same handle the page-level rows use, so "grab here to move it" means the same
          thing everywhere on the canvas. */}
      <button
        type="button"
        aria-label={`Move ${child.title || child.ref}`}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab text-[var(--ff-text-muted)] hover:text-[var(--ff-text)]"
        {...attributes}
        {...listeners}
      >
        <Icon name="drag" size={13} />
      </button>

      <span className="flex-1 truncate">{child.title || child.ref}</span>
      <span className="text-[var(--ff-text-muted)]">{child.type}</span>

      <IconButton
        label={`Delete ${child.title || child.ref}`}
        onClick={(e) => { e.stopPropagation(); removeElement(child.ref); }}
      >
        <Icon name="trash" size={13} />
      </IconButton>
    </li>
  );
}

function SortableItem({
  element, position, rowSize,
}: { element: FormElement; position: number; rowSize: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: element.ref, data: { kind: 'element', ref: element.ref } });

  const selectedRef = useBuilder((s) => s.selectedRef);
  const select = useBuilder((s) => s.select);
  const removeElement = useBuilder((s) => s.removeElement);
  const duplicateElement = useBuilder((s) => s.duplicateElement);
  const edit = useElementEdit(element.ref);

  const selected = selectedRef === element.ref;
  const isContent = element.kind === 'content';
  const meta = typeMeta(element.type);

  // A full row has no width left to give, and a question cannot be dropped beside itself.
  const { active } = useDndContext();
  const canSitBeside = Boolean(active)
    && active?.id !== element.ref
    && rowSize < MAX_PER_ROW;

  // Content blocks have no title. Showing "Untitled" made them look broken; a stripped
  // preview of the HTML is what the author actually recognises.
  const preview = isContent
    ? (element.html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    : element.title ?? '';

  const icon = (isContent ? 'heading' : meta?.icon ?? 'text') as IconName;
  const tint = isContent ? CATEGORY_TINT.Layout : CATEGORY_TINT[meta?.category ?? 'Text'];

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={clsx(
        'group relative h-full rounded-lg border bg-[var(--ff-surface)] transition-colors',
        selected
          ? 'border-[var(--ff-primary)] ring-2 ring-[var(--ff-primary)]/15'
          : 'border-[var(--ff-border)] hover:border-[var(--ff-border-strong)]',
        isDragging && 'opacity-50 shadow-lg',
      )}
    >
      {/*
        The side targets, live only while something is being dragged.
        Dropping on one puts the two questions on the same row and works the widths out,
        so a side-by-side layout needs no columns block and no trip to the width setting.
        They are not rendered at rest, or they would swallow clicks meant for the card.
      */}
      {canSitBeside && (
        <>
          <SideZone element={element} side="left" />
          <SideZone element={element} side="right" />
        </>
      )}
      {/*
        Wraps rather than squeezes. At a third of the width there is not room for a grip, an
        icon, a title and three buttons on one line, and the title was the thing that lost —
        truncated to "Firs…" while the buttons kept their full size. The actions drop to
        their own line instead.
      */}
      <div className="flex flex-wrap items-start gap-x-2 gap-y-1 p-2.5">
        <button
          {...attributes}
          {...listeners}
          aria-label={`Reorder ${preview || element.ref}`}
          className="mt-0.5 cursor-grab rounded p-0.5 text-[var(--ff-text-muted)]
                     opacity-40 transition-opacity hover:bg-[var(--ff-surface-raised)]
                     group-hover:opacity-100 active:cursor-grabbing"
        >
          <Icon name="drag" />
        </button>

        <span className={clsx(
          'mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md', tint,
        )}>
          <Icon name={icon} />
        </span>

        <button
          onClick={() => select(element.ref)}
          className="min-w-24 flex-1 text-left"
          aria-current={selected ? 'true' : undefined}
        >
          <div className="flex flex-wrap items-center gap-1.5">
            {!isContent && (
              <span className="text-[11px] tabular-nums text-[var(--ff-text-muted)]">
                {position}.
              </span>
            )}
            <span className={clsx(
              'truncate text-sm',
              isContent ? 'text-[var(--ff-text-muted)] italic' : 'font-medium',
            )}>
              {preview || <span className="italic text-[var(--ff-text-muted)]">Empty</span>}
            </span>
            {element.required && (
              <span className="text-[var(--ff-danger)]" title="Required" aria-label="Required">*</span>
            )}
            {element.pii && <Badge tone="warning">{element.pii}</Badge>}
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px]
                          text-[var(--ff-text-muted)]">
            <span>{isContent ? 'Content block' : meta?.label ?? element.type}</span>
            <code>{element.ref}</code>
            {element.choices && <span>{element.choices.length} choices</span>}
            {element.rows && <span>{element.rows.length} rows</span>}

            {/* A matrix is rows by columns; summarising it by its choices described a list
                it does not have and hid the empty columns that block publishing. */}
            {element.columns && (
              <span className={element.columns.length === 0 ? 'text-[var(--ff-danger)]' : undefined}>
                {element.columns.length} columns
              </span>
            )}
          </div>
        </button>

        {/*
          Always visible, not revealed on hover. These are the actions taken most often, and
          a control you have to find by hovering is a control most people never find at all —
          it also leaves touch and keyboard users guessing.
        */}
        <div className="ml-auto flex items-center gap-0.5">
          {!isContent && (
            <IconButton
              label={element.required ? `Make ${element.ref} optional` : `Make ${element.ref} required`}
              aria-pressed={!!element.required}
              on={!!element.required}
              onClick={() => edit('Toggle required', (el) => { el.required = !el.required; })}
            >
              <Icon name="star" />
            </IconButton>
          )}
          <IconButton label={`Duplicate ${element.ref}`}
                      onClick={() => duplicateElement(element.ref)}>
            <Icon name="copy" />
          </IconButton>
          <IconButton label={`Delete ${element.ref}`}
                      onClick={() => removeElement(element.ref)}>
            <Icon name="trash" />
          </IconButton>
        </div>
      </div>

      {/*
        Only the selected question expands. Every row carrying its own editor would turn a
        scannable list of thirty questions into a page nobody can navigate, and the summary
        row is what makes the canvas readable at a glance.
      */}
      {selected && !isContent && <InlineEditor element={element} />}
    </div>
  );
}

/**
 * One edge of a question card, as a drop target.
 *
 * A quarter of the width on each side, leaving the middle half to the ordinary vertical
 * reorder — narrow enough that dropping "below" still means below, wide enough to hit
 * without aiming.
 */
function SideZone({ element, side }: { element: FormElement; side: 'left' | 'right' }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `side:${side}:${element.ref}`,
    data: { kind: 'side', ref: element.ref, side },
  });

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'absolute inset-y-0 z-10 w-1/4 rounded-lg transition-colors',
        side === 'left' ? 'left-0' : 'right-0',
        // The card being dragged sits under the cursor and covers a hairline at the very
        // edge, so the whole zone tints as well. It is the only thing telling somebody that
        // dropping here means "beside" rather than "below".
        isOver && 'bg-[var(--ff-primary)]/12',
      )}
    >
      {isOver && (
        <span
          className={clsx(
            'absolute inset-y-1 w-1 rounded-full bg-[var(--ff-primary)]',
            side === 'left' ? 'left-0' : 'right-0',
          )}
        />
      )}
    </div>
  );
}

/** Shown under the cursor while dragging an existing question. */
export function ElementPreview({ element }: { element: FormElement }) {
  const meta = typeMeta(element.type);
  const isContent = element.kind === 'content';
  const label = isContent
    ? (element.html ?? '').replace(/<[^>]*>/g, ' ').trim()
    : element.title || element.ref;

  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-[var(--ff-primary)]
                    bg-[var(--ff-surface)] px-3 py-2 text-sm font-medium shadow-xl">
      <span className={clsx(
        'grid h-7 w-7 place-items-center rounded-md',
        CATEGORY_TINT[meta?.category ?? 'Layout'],
      )}>
        <Icon name={(meta?.icon ?? 'heading') as IconName} />
      </span>
      <span className="max-w-56 truncate">{label}</span>
    </div>
  );
}

export type { QuestionType };
